import { Show } from 'solid-js'
import Card from '~/components/Card'
import Icon from '~/components/Icon'
import ListEditor from '~/layers/ListEditor'
import type { ListEditorColumnType } from '~/types/ListEditor'
import type { TabCatalogosProps } from './types'

// ── Configuración de columnas para los editores visuales ──────────────────────
const CURRENCIES_COLUMNS: ListEditorColumnType[] = [
  { name: 'code', label: 'Código', placeholder: 'COP' },
  { name: 'name', label: 'Nombre', placeholder: 'Peso Colombiano' },
  { name: 'symbol', label: 'Símbolo', placeholder: '$' },
  { name: 'locale', label: 'Locale', placeholder: 'es-CO' },
  { name: 'decimalDigits', label: 'Decimales', type: 'number', placeholder: '2' },
  { name: 'symbolPosition', label: 'Posición símbolo', placeholder: 'before' },
  { name: 'groupSeparator', label: 'Sep. miles', placeholder: '.' },
  { name: 'decimalSeparator', label: 'Sep. decimal', placeholder: ',' }
]

const DOCUMENT_TYPES_COLUMNS: ListEditorColumnType[] = [
  { name: 'code', label: 'Código', placeholder: 'CC' },
  { name: 'label', label: 'Nombre', placeholder: 'Cédula de Ciudadanía' },
  { name: 'pattern', label: 'Patrón', placeholder: '(opcional)', optional: true }
]

const BANK_ACCOUNT_TYPES_COLUMNS: ListEditorColumnType[] = [
  { name: 'value', label: 'Valor', placeholder: 'Ahorros' },
  { name: 'label', label: 'Nombre', placeholder: 'Cuenta de ahorros' }
]

const ADDRESS_LABELS_COLUMNS: ListEditorColumnType[] = [
  { name: 'value', label: 'Valor', placeholder: 'fiscal' },
  { name: 'label', label: 'Nombre', placeholder: 'Fiscal' }
]

const CUSTOMER_CLASSIFICATIONS_COLUMNS: ListEditorColumnType[] = [
  { name: 'value', label: 'Valor', placeholder: 'A' },
  { name: 'label', label: 'Nombre', placeholder: 'A - Prioritario' },
  { name: 'order', label: 'Orden', type: 'number', placeholder: '1' }
]

/** Pestaña Catálogos: monedas, documentos, cuentas, etiquetas y clasificaciones configurables. */
function TabCatalogos(props: TabCatalogosProps) {
  const { form } = props

  return (
    <Card>
      <Card.Title>
        <div class="flex items-center">
          <Icon icon="material-symbols-light:format-list-bulleted" size={20} class="mr-2" />
          <h2 class="text-xl font-semibold">Catálogos de la empresa</h2>
        </div>
      </Card.Title>
      <Card.Body>
        <p class="text-sm text-foreground-subtle mb-4">Estos valores alimentan los selectores de esta empresa (monedas, tipos de documento, cuentas bancarias...).</p>

        <div class="space-y-6">
          <div>
            <h3 class="text-sm font-semibold text-foreground mb-2">Monedas</h3>
            <ListEditor
              value={(form.values().currenciesJson as Record<string, unknown>[]) ?? []}
              columns={CURRENCIES_COLUMNS}
              uniqueKey="code"
              addLabel="Añadir moneda"
              onChange={items => form.setValue('currenciesJson', items)}
            />
            <Show when={form.error('currenciesJson')}>
              <p class="mt-1 text-xs text-danger">{form.error('currenciesJson')}</p>
            </Show>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-foreground mb-2">Tipos de documento</h3>
            <ListEditor
              value={(form.values().documentTypesJson as Record<string, unknown>[]) ?? []}
              columns={DOCUMENT_TYPES_COLUMNS}
              uniqueKey="code"
              addLabel="Añadir tipo de documento"
              onChange={items => form.setValue('documentTypesJson', items)}
            />
            <Show when={form.error('documentTypesJson')}>
              <p class="mt-1 text-xs text-danger">{form.error('documentTypesJson')}</p>
            </Show>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-foreground mb-2">Tipos de cuenta bancaria</h3>
            <ListEditor
              value={(form.values().bankAccountTypesJson as Record<string, unknown>[]) ?? []}
              columns={BANK_ACCOUNT_TYPES_COLUMNS}
              uniqueKey="value"
              addLabel="Añadir tipo de cuenta"
              onChange={items => form.setValue('bankAccountTypesJson', items)}
            />
            <Show when={form.error('bankAccountTypesJson')}>
              <p class="mt-1 text-xs text-danger">{form.error('bankAccountTypesJson')}</p>
            </Show>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-foreground mb-2">Etiquetas de dirección</h3>
            <ListEditor
              value={(form.values().addressLabelsJson as Record<string, unknown>[]) ?? []}
              columns={ADDRESS_LABELS_COLUMNS}
              uniqueKey="value"
              addLabel="Añadir etiqueta"
              onChange={items => form.setValue('addressLabelsJson', items)}
            />
            <Show when={form.error('addressLabelsJson')}>
              <p class="mt-1 text-xs text-danger">{form.error('addressLabelsJson')}</p>
            </Show>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-foreground mb-2">Clasificaciones de cliente</h3>
            <ListEditor
              value={(form.values().customerClassificationsJson as Record<string, unknown>[]) ?? []}
              columns={CUSTOMER_CLASSIFICATIONS_COLUMNS}
              uniqueKey="value"
              addLabel="Añadir clasificación"
              onChange={items => form.setValue('customerClassificationsJson', items)}
            />
            <Show when={form.error('customerClassificationsJson')}>
              <p class="mt-1 text-xs text-danger">{form.error('customerClassificationsJson')}</p>
            </Show>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default TabCatalogos
